/**
 * Tag.tsx — Compact label/badge for statuses, methods, confidence scores.
 *
 * Kinds: cyan, success, warning, danger, neutral, purple
 *         (purple used sparingly for admin-mode indicators)
 *
 * Two visual treatments:
 *   - soft  (default): tinted background + colored text + subtle border
 *   - solid: solid color background, used for strong status indicators
 *
 * Usage:
 *   <Tag kind="success">Active</Tag>
 *   <Tag kind="warning" dot>Pending review</Tag>
 *   <Tag kind="cyan" variant="solid">Live</Tag>
 */

import { ReactNode } from 'react';
import { theme } from '../../theme';

export type TagKind = 'cyan' | 'success' | 'warning' | 'danger' | 'neutral' | 'purple';
type TagVariant = 'soft' | 'solid';
type TagSize = 'sm' | 'md';

interface TagProps {
  kind?: TagKind;
  variant?: TagVariant;
  size?: TagSize;
  dot?: boolean;
  mono?: boolean;
  uppercase?: boolean;
  children: ReactNode;
}

function colors(kind: TagKind) {
  switch (kind) {
    case 'cyan':    return { bg: theme.brand.cyanSoft,       color: theme.brand.cyanLight,   border: theme.semantic.infoBorder };
    case 'success': return { bg: theme.semantic.successSoft, color: theme.semantic.success,  border: theme.semantic.successBorder };
    case 'warning': return { bg: theme.semantic.warningSoft, color: theme.semantic.warning,  border: theme.semantic.warningBorder };
    case 'danger':  return { bg: theme.semantic.dangerSoft,  color: theme.semantic.danger,   border: theme.semantic.dangerBorder };
    case 'purple':  return { bg: 'rgba(139,92,246,0.10)',    color: '#A78BFA',               border: 'rgba(139,92,246,0.30)' };
    case 'neutral': return { bg: 'rgba(148,163,184,0.08)',   color: theme.text.secondary,    border: theme.borderColor.default };
  }
}

export function Tag({
  kind = 'neutral',
  variant = 'soft',
  size = 'sm',
  dot = false,
  mono = false,
  uppercase = true,
  children,
}: TagProps) {
  const c = colors(kind);
  const sizing = size === 'sm' ? { padding: '2px 7px', fontSize: '10px' } : { padding: '4px 10px', fontSize: '11px' };

  const style: React.CSSProperties = variant === 'soft'
    ? { background: c.bg, color: c.color, border: `1px solid ${c.border}` }
    : { background: c.color, color: theme.text.inverse, border: `1px solid ${c.color}` };

  return (
    <span style={{
      display: 'inline-flex',
      alignItems: 'center',
      gap: 5,
      padding: sizing.padding,
      borderRadius: 4,
      fontSize: sizing.fontSize,
      fontWeight: theme.fontWeight.medium,
      fontFamily: mono ? theme.font.data : theme.font.ui,
      letterSpacing: uppercase ? '0.04em' : 'normal',
      textTransform: uppercase ? 'uppercase' : 'none',
      whiteSpace: 'nowrap',
      lineHeight: 1.2,
      ...style,
    }}>
      {dot && (
        <span style={{
          width: 6, height: 6,
          borderRadius: '50%',
          background: variant === 'solid' ? theme.text.inverse : c.color,
          boxShadow: variant === 'soft' ? `0 0 6px ${c.color}80` : 'none',
          flexShrink: 0,
        }} />
      )}
      {children}
    </span>
  );
}

/* ═══════════════ CONFIDENCE SCORE TAG ═══════════════ */
/* Specialized tag for match confidence scores. Color picks kind automatically. */

export function ConfidenceTag({ score }: { score: number | null | undefined }) {
  if (score == null) return <span style={{ color: theme.text.tertiary, fontFamily: theme.font.data, fontSize: 13 }}>—</span>;
  const pct = Math.round(score * 100);
  const kind: TagKind = pct >= 90 ? 'success' : pct >= 65 ? 'warning' : 'danger';
  return <Tag kind={kind} mono uppercase={false} size="sm">{pct}%</Tag>;
}

/* ═══════════════ MATCH METHOD TAG ═══════════════ */
/* Specialized tag for match method values (deterministic, fuzzy, etc). */

const methodConfig: Record<string, { label: string; kind: TagKind }> = {
  deterministic:    { label: 'EXACT',  kind: 'success' },
  fuzzy:            { label: 'SMART',  kind: 'cyan' },
  geo:              { label: 'GPS',    kind: 'cyan' },
  manual_approved:  { label: 'MANUAL', kind: 'purple' },
  llm_approved:     { label: 'AI MATCH', kind: 'purple' },
};

export function MethodTag({ method }: { method: string }) {
  const config = methodConfig[method] ?? { label: (method || '').toUpperCase(), kind: 'neutral' as const };
  return <Tag kind={config.kind} mono size="sm">{config.label}</Tag>;
}
